/*
 * Copyright (c) Volvo Group. All rights reserved.
 * SPDX-License-Identifier: ISC
 */

import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useNavigate,
} from "react-router";
import { type ReactNode, StrictMode, Suspense } from "react";
import type { Route } from "./+types/root";
import { store } from "~/store/Store";
import { Provider } from "react-redux";
import { MsalProvider } from "@azure/msal-react";
import { ReactQueryProvider } from "./core/Http/QueryClient";
import { getMsalInstance } from "~/core/Security/authConfig";
import { CustomNavigationClient } from "~/core/Security/NavigationClient";
import { i18nInit } from "~/i18n";

export const links: Route.LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  { rel: "preconnect", href: "https://cdn.designsystem.volvogroup.com" },
  {
    rel: "preconnect",
    href: "https://cdn.designsystem.volvogroup.com",
    crossOrigin: "anonymous",
  },
  {
    /** Volvo font */
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Noto+Sans:ital,wdth,wght@0,62.5..100,100..900;1,62.5..100,100..900&display=swap",
  },
  {
    /** Mack & Renault font */
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,400;0,500;1,400;1,500&display=swap",
  },
  {
    rel: "manifest",
    href: "./manifest.json",
  },
  {
    rel: "icon",
    sizes: "32x32",
    href: "https://cdn.designsystem.volvogroup.com/1-1-0/favicons/volvo/favicon.ico",
  },
  {
    rel: "icon",
    type: "image/svg+xml",
    href: "https://cdn.designsystem.volvogroup.com/1-1-0/favicons/volvo/favicon.svg",
  },
  {
    rel: "apple-touch-icon",

    href: "https://cdn.designsystem.volvogroup.com/1-1-0/favicons/volvo/apple-touch-icon.png",
  },
  {
    rel: "stylesheet",

    href: "https://cdn.designsystem.volvogroup.com/1-1-0/css/volvo/typefaces.css",
  },
];

const msalInstance = await getMsalInstance();

i18nInit();

export function Layout({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const navigationClient = new CustomNavigationClient(navigate);
  msalInstance.setNavigationClient(navigationClient);

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#141414" />
        <meta name="description" content="Cloud Adoption Assistant" />
        <title>Cloud Adoption Assistant</title>

        <Meta />
        <Links />
      </head>
      <body suppressHydrationWarning={true}>
        <StrictMode>
          <Suspense fallback={"loading"}>
            <ReactQueryProvider>
              <Provider store={store}>
                <MsalProvider instance={msalInstance}>{children}</MsalProvider>
              </Provider>
            </ReactQueryProvider>
          </Suspense>
        </StrictMode>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return <Outlet />;
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    const Error404 = 404;
    message = error.status === Error404 ? "404" : "Error";
    details =
      error.status === Error404
        ? "The requested page could not be found."
        : error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    // eslint-disable-next-line prefer-destructuring
    stack = error.stack;
  }

  return (
    <main className="pt-16 p-4 container mx-auto">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre className="w-full p-4 overflow-x-auto">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
